import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { renderSlot } from 'renderer/viewer/three/renderSlot'
import { getItemModelName } from '../../../mineflayer/items'
import { armor as armorTable } from '../../armorValues'
import styles from './ReplayHudPanel.module.css'

/* Replay HUD — wood crate + glass panel on the holo slab. One original MC
   sprite per vital (highlighted), then a mono strip + number in our style. */

type SlotItem = {
  name: string
  count?: number
  metadata?: number
  nbt?: unknown
} | null

type VitalKind = 'health' | 'armor' | 'food'

const slotKey = (item: SlotItem, index: number) => (item ? `${index}|${item.name}|${item.count ?? 1}|${item.metadata ?? 0}` : `${index}|empty`)

const atlasCache = new Map<string, Promise<CanvasImageSource | null>>()

const loadAtlas = async (path: string): Promise<CanvasImageSource | null> => {
  const cached = atlasCache.get(path)
  if (cached) return cached
  const promise = (async () => {
    try {
      const manager = appViewer.resourcesManager
      let source: unknown
      switch (path) {
        case 'items': {
          source = manager?.itemsAtlasParser?.latestImage
          break
        }
        case 'blocks': {
          source = manager?.blocksAtlasParser?.latestImage
          break
        }
        case 'gui': {
          source = manager?.currentResources?.guiAtlas?.image
          break
        }
        default:
      }
      if (!source) return null
      if (typeof source === 'string') {
        const image = new Image()
        image.src = source
        await image.decode()
        return image
      }
      return source as CanvasImageSource
    } catch {
      return null
    }
  })()
  atlasCache.set(path, promise)
  return promise
}

const drawCubeFace = (
  ctx: CanvasRenderingContext2D,
  size: number,
  side: string,
  draw: (x: number, y: number, w: number, h: number) => void,
) => {
  ctx.save()
  const x = -size * 0.4
  const y = -size * 0.5
  const s = size / 2
  switch (side) {
    case 'left': {
      ctx.transform(0.9, 0.5, 0, 1, x, y)
      draw(s, s, s, s)
      ctx.fillStyle = 'rgba(0, 0, 0, 0.35)'
      ctx.fillRect(s, s, s, s)
      break
    }
    case 'right': {
      ctx.transform(0.9, -0.5, 0, 1, x, y)
      draw(s + s - 1, s * 3 - 1, s, s)
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
      ctx.fillRect(s + s - 1, s * 3 - 1, s, s)
      break
    }
    case 'top': {
      ctx.transform(0.9, -0.5, 0.9, 0.5, x, y)
      draw(-s - 1, s * 2, s, s)
      break
    }
  }
  ctx.restore()
}

const drawSlotIcon = async (canvas: HTMLCanvasElement, item: SlotItem) => {
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  if (!item) return
  if (!appViewer.resourcesManager?.currentResources?.itemsRenderer) return
  let props
  try {
    const modelName = getItemModelName(
      item as any,
      { 'minecraft:display_context': 'gui' },
      appViewer.resourcesManager,
      appViewer.playerState.reactive,
    )
    props = renderSlot({ modelName, originalItemName: item.name }, appViewer.resourcesManager)
  } catch {
    return
  }
  ctx.imageSmoothingEnabled = false
  if (props.blockData) {
    for (const side of ['left', 'right', 'top']) {
      const face = props.blockData[side]
      if (!face?.slice) continue
      // eslint-disable-next-line no-await-in-loop
      const image = await loadAtlas(face.path ?? 'blocks')
      if (!image) continue
      const [sx, sy, sw, sh] = face.slice
      drawCubeFace(ctx, canvas.width, side, (dx, dy, dw, dh) => {
        ctx.imageSmoothingEnabled = false
        ctx.drawImage(image, sx, sy, sw, sh, dx, dy, dw, dh)
      })
    }
  } else if (props.slice) {
    const image = await loadAtlas(props.texture)
    if (!image) return
    const [sx, sy, sw, sh] = props.slice
    const inset = canvas.width / 16
    ctx.drawImage(image, sx, sy, sw, sh, inset, inset, canvas.width - inset * 2, canvas.height - inset * 2)
  }
}

const SlotIcon = ({ item, redrawToken }: { item: SlotItem, redrawToken: number }) => {
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    let cancelled = false
    void drawSlotIcon(canvas, item).then(() => {
      if (cancelled) { /* no-op */ }
    })
    return () => { cancelled = true }
  }, [item, redrawToken])
  return <canvas ref={ref} width={64} height={64} className={styles.slotCanvas} />
}

const finiteOr = (value: unknown, fallback: number) => (typeof value === 'number' && Number.isFinite(value) ? value : fallback)

const readArmorPoints = () => {
  let points = 0
  for (const slotIndex of [5, 6, 7, 8]) {
    const item = bot.inventory.slots[slotIndex]
    if (!item) continue
    const [material, piece] = item.name.split('_')
    points += armorTable[material]?.[piece] ?? 0
  }
  return points
}

const readHotbar = (): SlotItem[] => {
  const start = bot.inventory.hotbarStart ?? 36
  return Array.from({ length: 9 }, (_, i) => (bot.inventory.slots[start + i] ?? null) as SlotItem)
}

/* One hero sprite per row from gui/icons.png (256 sheet, 9px cells). */
const VITAL_SPRITE: Record<VitalKind, { x: number, y: number }> = {
  health: { x: 52, y: 0 },
  armor: { x: 34, y: 9 },
  food: { x: 52, y: 27 },
}

/* Square cells per bar — same 16px footprint as the icon cell («квадратики,
   симметричненько»). 8 is the most that fits the slab width next to the
   icon: 16 + 6 + 8×16 + 7×2 = 164px of ~179px inner. */
const BAR_SEGMENTS = 8

const segmentState = (value: number, max: number, i: number): 'full' | 'half' | 'empty' => {
  const lit = (value / Math.max(1, max)) * BAR_SEGMENTS - i
  if (lit >= 0.75) return 'full'
  if (lit >= 0.25) return 'half'
  return 'empty'
}

export default function ReplayHudPanel () {
  const [health, setHealth] = useState(() => finiteOr(bot?.health, 20))
  const [food, setFood] = useState(() => finiteOr(bot?.food, 20))
  const [armorPoints, setArmorPoints] = useState(() => (bot ? readArmorPoints() : 0))
  const [hotbar, setHotbar] = useState<SlotItem[]>(() => (bot ? readHotbar() : Array.from({ length: 9 }, () => null)))
  const [selected, setSelected] = useState(() => bot?.quickBarSlot ?? 0)
  const [redrawToken, setRedrawToken] = useState(0)

  useEffect(() => {
    if (!bot) return
    const upVitals = () => {
      setHealth(finiteOr(bot.health, 20))
      setFood(finiteOr(bot.food, 20))
    }
    const upInventory = () => {
      setArmorPoints(readArmorPoints())
      setHotbar(readHotbar())
    }
    const upSelected = () => setSelected(bot.quickBarSlot)
    const upAtlas = () => {
      atlasCache.clear()
      setRedrawToken(t => t + 1)
    }
    bot.on('health', upVitals)
    bot.inventory.on('updateSlot', upInventory)
    bot.on('heldItemChanged' as any, upSelected)
    appViewer.resourcesManager?.on('assetsTexturesUpdated', upAtlas)
    appViewer.resourcesManager?.on('assetsInventoryReady', upAtlas)
    upVitals(); upInventory(); upSelected()
    return () => {
      bot.off('health', upVitals)
      bot.inventory.off('updateSlot', upInventory)
      bot.off('heldItemChanged' as any, upSelected)
      appViewer.resourcesManager?.off('assetsTexturesUpdated', upAtlas)
      appViewer.resourcesManager?.off('assetsInventoryReady', upAtlas)
    }
  }, [])

  const vitals: Array<{ kind: VitalKind, value: number, max: number }> = [
    { kind: 'health', value: health, max: 20 },
    { kind: 'armor', value: armorPoints, max: 20 },
    { kind: 'food', value: food, max: 20 },
  ]

  return (
    <div className={styles.panelWrap} data-replay-ui data-holo-hud-panel>
      <div className={styles.panel}>
        <div className={styles.vitalsRow}>
          {vitals.map(({ kind, value, max }) => {
            const sprite = VITAL_SPRITE[kind]
            const low = kind === 'health' && value <= 6
            return (
              <div
                key={kind}
                className={styles.vital}
                data-kind={kind}
                data-low={low ? 'true' : undefined}
              >
                <span
                  className={styles.vitalIcon}
                  style={{ '--vx': sprite.x, '--vy': sprite.y } as CSSProperties}
                  aria-hidden="true"
                />
                <div className={styles.vitalBarTrack}>
                  {Array.from({ length: BAR_SEGMENTS }, (_, i) => (
                    <i key={i} className={styles.vitalSeg} data-state={segmentState(value, max, i)} />
                  ))}
                </div>
              </div>
            )
          })}
        </div>
        <div className={styles.hotbarRow}>
          {hotbar.map((item, i) => (
            <div
              key={slotKey(item, i)}
              className={[styles.slot, i === selected ? styles.slotSelected : '', !item ? styles.slotEmpty : ''].filter(Boolean).join(' ')}
            >
              <SlotIcon item={item} redrawToken={redrawToken} />
              {item && (item.count ?? 1) > 1 && <span className={styles.slotCount}>{item.count}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
